import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5timeline from '@amcharts/amcharts5/timeline';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { Subscription } from 'rxjs';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Serpentine Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-serpentine-timeline-graph',
  standalone: false,
  templateUrl: './serpentine-timeline-graph.component.html',
  styleUrl: './serpentine-timeline-graph.component.scss'
})
export class SerpentineTimelineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private serpentineTimelineGraphReportSubscription: any;
  private isSerpentineTimelineViewInitialized = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.serpentineTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Serpentine Timeline Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isSerpentineTimelineViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isSerpentineTimelineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isSerpentineTimelineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    console.log('Creating serpentine timeline chart with data:', data);
    
    this.root = this._baseChartService.createRoot('serpentine-timeline');
    this._baseChartService.attachExporting(this.root, { 
      filePrefix: 'serpentine-timeline',
      data: data
    });

    this.root.setThemes([am5themes_Animated.new(this.root)]);

    const wrapper = this.root.container.children.push(am5.Container.new(this.root, {
      width: am5.percent(100),
      height: am5.percent(100),
      layout: this.root.verticalLayout
    }));
    const chartHost = wrapper.children.push(am5.Container.new(this.root, {
      width: am5.percent(100),
      height: am5.percent(82)
    }));

    // Create serpentine chart
    const chart = chartHost.children.push(
      am5timeline.SerpentineChart.new(this.root, {
        width: am5.percent(100),
        height: am5.percent(100),
        levelCount: 3,
        startLocation: 0.2,
        endLocation: 1,
        wheelY: "zoomX"
      })
    );

   /*  chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
      orientation: "horizontal"
    })); */

    // Create Y axis renderer
    let yRenderer = am5timeline.AxisRendererCurveY.new(this.root, {});

    yRenderer.labels.template.setAll({
      centerY: am5.p50,
      centerX: am5.p100,
      fontSize: 11
    });

    yRenderer.grid.template.set("forceHidden", true);

    // Create X axis renderer
    let xRenderer = am5timeline.AxisRendererCurveX.new(this.root, {
      yRenderer: yRenderer,
      strokeDasharray: [2, 3],
      strokeOpacity: 0.5,
      stroke: am5.color(0x000000)
    });

    xRenderer.labels.template.setAll({
      centerY: am5.p50,
      fontSize: 11,
      minPosition: 0.01
    });

    xRenderer.labels.template.setup = (target: any) => {
      target.set("layer", 30);
      target.set("background", am5.Rectangle.new(this.root, {
        fill: am5.color(0xffffff),
        fillOpacity: 1
      }));
    };

    // Create axes
    let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(this.root, {
      maxDeviation: 0,
      categoryField: "category",
      renderer: yRenderer
    }) as any);

    let xAxis = chart.xAxes.push(am5xy.DateAxis.new(this.root, {
      baseInterval: { timeUnit: "day", count: 1 },
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(this.root, {})
    }) as any);

    // Transform data for serpentine timeline
    const timelineData = this.transformDataForSerpentineTimeline(data);
    console.log('Serpentine timeline data:', timelineData);

    // Add series
    let series = chart.series.push(am5timeline.CurveColumnSeries.new(this.root, {
      xAxis: xAxis,
      yAxis: yAxis,
      baseAxis: yAxis,
      valueXField: "end",
      openValueXField: "start",
      categoryYField: "category",
      layer: 30
    }));

    series.columns.template.setAll({
      height: am5.percent(10),
      strokeOpacity: 0
    });

    // Configure tooltip
    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "[bold]{category}[/]\nDuration: {value} days\nTask: {task}",
      pointerOrientation: "horizontal"
    });
    series.set('tooltip', tooltip);

    // Add bullets to series
    series.bullets.push((root: am5.Root, series: any, dataItem: any) => {
      let circle = am5.Circle.new(root, {
        radius: 4,
        fill: chart.get("colors")?.getIndex(series.dataItems.indexOf(dataItem) || 0),
        strokeWidth: 2,
        strokeOpacity: 0.5,
        layer: 30
      });

      // Add tooltip to bullet
      circle.set("tooltipText", `[bold]{category}[/]\nDuration: {value} days`);

      return am5.Bullet.new(root, {
        sprite: circle,
        locationX: 0,
        locationY: 0.5
      });
    });

    series.bullets.push((root: am5.Root, series: any, dataItem: any) => {
      let circle = am5.Circle.new(root, {
        radius: 4,
        fill: chart.get("colors")?.getIndex(series.dataItems.indexOf(dataItem) || 0),
        strokeWidth: 2,
        strokeOpacity: 0.5,
        layer: 30
      });

      // Add tooltip to bullet
      circle.set("tooltipText", `[bold]{category}[/]\nDuration: {value} days`);

      return am5.Bullet.new(root, {
        sprite: circle,
        locationX: 1,
        locationY: 0.5
      });
    });

    // Add column fill adapter
    series.columns.template.adapters.add("fill", (fill: any, target: any) => {
      return chart.get("colors")?.getIndex(series.dataItems.indexOf(target.dataItem) || 0);
    });

    // Set data
    series.data.setAll(timelineData);

    // Set Y axis data
    const categories = [...new Set(timelineData.map(item => item.category))];
    yAxis.data.setAll(categories.map(category => ({ category })));

    // Add cursor for better tooltip interaction
    chart.set("cursor", am5timeline.CurveCursor.new(this.root, {
      behavior: "zoomX",
      xAxis: xAxis,
      yAxis: yAxis
    }));

    const legend = wrapper.children.push(
      this._baseChartService.createLegend(this.root)
    );
    legend.setAll({
      width: am5.percent(100),
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 8,
      marginBottom: 8
    });
    legend.markers.template.setAll({ width: 12, height: 12 });
    legend.labels.template.setAll({
      text: '{categoryY}',
      fontSize: 12,
      oversizedBehavior: 'truncate',
      maxWidth: 180
    });
    legend.valueLabels.template.set('forceHidden', true);
    legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) {
        return fill;
      }
      const index = series.dataItems.indexOf(dataItem);
      return index >= 0 ? chart.get('colors')?.getIndex(index) : fill;
    });
    legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) {
        return stroke;
      }
      const index = series.dataItems.indexOf(dataItem);
      return index >= 0 ? chart.get('colors')?.getIndex(index) : stroke;
    });
    const updateLegend = () => {
      if (series.dataItems?.length > 0) {
        legend.data.setAll(series.dataItems);
      }
    };
    series.events.on('datavalidated', updateLegend);
    updateLegend();
    this._baseChartService.setupLegendAnimation(legend);

    // Trigger chart animation
    setTimeout(() => {
      series.appear(1000);
      chart.appear(1000, 100);
    }, 100);
    
    console.log('Serpentine timeline chart created successfully');
  }
  

    
  
  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  private transformDataForSerpentineTimeline(data: BarChartData[]): any[] {
    const timelineData: any[] = [];
    console.log('Transforming data for serpentine timeline:', data);

    if (data.length === 0) {
      console.log('No data provided');
      return timelineData;
    }

    // Get the first item to determine field names dynamically
    const firstItem = data[0];
    const keys = Object.keys(firstItem);
    console.log('Available fields:', keys);

    if (keys.length < 2) {
      console.log('Not enough fields in data');
      return timelineData;
    }

    // Assume first field is category/label, second field is value
    const categoryField = keys[0];
    const valueField = keys[1];
    
    console.log('Using fields - Category:', categoryField, 'Value:', valueField);

    // Use the original data to create timeline entries
    data.forEach((item: BarChartData, index: number) => {
      console.log('Processing item for serpentine timeline:', item);
      
      // Get category and value dynamically
      const category = item[categoryField];
      const value = item[valueField];
      
      // Skip empty categories or zero/null values
      if (!category || category === '' || value === 0 || value === null || value === undefined) {
        console.log('Skipping item with empty category or invalid value:', item);
        return;
      }
      
      if (value !== undefined && value !== null) {
        // Create timeline entry with start and end dates
        const baseDate = new Date();
        const startDate = new Date(baseDate);
        startDate.setDate(startDate.getDate() + (index * 7)); // Each item starts a week apart
        
        const endDate = new Date(startDate);
        const numericValue = typeof value === 'number' ? Math.max(1, Math.min(value, 30)) : 7; // Limit to 1-30 days
        endDate.setDate(endDate.getDate() + numericValue);

        const timelineItem = {
          category: category,
          start: startDate.getTime(),
          end: endDate.getTime(),
          color: am5.ColorSet.new(this.root, {}).getIndex(index),
          task: valueField, // Use the actual field name as task description
          value: value
        };
        
        timelineData.push(timelineItem);
        console.log('Created serpentine timeline item:', timelineItem);
      }
    });

    // If no data was processed, create some sample data to show the chart structure
    if (timelineData.length === 0) {
      console.log('No valid data found for serpentine timeline, creating sample data');
      const currentDate = new Date();
      const sampleCategories = ['Sample Category 1', 'Sample Category 2', 'Sample Category 3'];

      sampleCategories.forEach((category, index) => {
        const startDate = new Date(currentDate);
        startDate.setDate(startDate.getDate() + (index * 7));
        
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 14);

        timelineData.push({
          category: category,
          start: startDate.getTime(),
          end: endDate.getTime(),
          color: am5.ColorSet.new(this.root, {}).getIndex(index),
          task: 'Sample Task',
          value: 14
        });
      });
    }

    console.log('Final serpentine timeline data:', timelineData);
    return timelineData;
  }
    
  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.serpentineTimelineGraphReportSubscription) {
      this.serpentineTimelineGraphReportSubscription.unsubscribe();
    }
  }
}
